import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Headphones, ArrowRight, Sparkles, Music } from 'lucide-react';
import { WaveVisualizer } from './WaveVisualizer';
import { useClickSound } from '../../hooks/useClickSound';
interface SoundTherapyStepProps {
  frequency?: number;
  frequencies?: number[];
  colorRGB?: string;
  secondaryColorRGB?: string;
  duration?: number;
  cost?: number;
  onComplete: () => void;
  isStandalone?: boolean;
  onClose?: () => void;
  isPremium?: boolean;
  soundName?: string;
  soundDesc?: string;
  soundIcon?: string;
}
export function SoundTherapyStep({
  frequency = 432,
  frequencies,
  colorRGB = '16, 185, 129',
  secondaryColorRGB = '255, 255, 255',
  duration = 30,
  cost = 0,
  onComplete,
  isStandalone = false,
  onClose,
  isPremium = false,
  soundName,
  soundDesc,
  soundIcon
}: SoundTherapyStepProps) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isActive, setIsActive] = useState(false);
  const [gyroTilt, setGyroTilt] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const {
    playClick,
    playBell
  } = useClickSound();
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const pannerRef = useRef<StereoPannerNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const freqArray = frequencies && frequencies.length > 0 ? frequencies : [frequency];
  const isDual = freqArray.length > 1;
  useEffect(() => {
    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (event.gamma !== null) {
        setGyroTilt(event.gamma);
        if (pannerRef.current && audioCtxRef.current) {
          let panValue = event.gamma / 45;
          panValue = Math.max(-1, Math.min(1, panValue));
          pannerRef.current.pan.setValueAtTime(panValue, audioCtxRef.current.currentTime);
        }
      }
    };
    if (isActive) {
      if (typeof DeviceOrientationEvent !== 'undefined' && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        ;
        (DeviceOrientationEvent as any).requestPermission().then((permissionState: string) => {
          if (permissionState === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation);
          }
        }).catch(console.error);
      } else {
        window.addEventListener('deviceorientation', handleOrientation);
      }
    }
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [isActive]);
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (isActive && timeLeft === 0 && !isFinished) {
      setIsFinished(true);
      setIsActive(false);
      stopAudio();
      playBell();
    }
    return () => clearInterval(timer);
  }, [isActive, timeLeft, isFinished, playBell]);
  const startAudio = () => {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      audioCtxRef.current = new AudioContext();
      gainRef.current = audioCtxRef.current.createGain();
      pannerRef.current = audioCtxRef.current.createStereoPanner();
      const targetVolume = isDual ? 0.3 : 0.5;
      gainRef.current.gain.setValueAtTime(0, audioCtxRef.current.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(targetVolume, audioCtxRef.current.currentTime + 2);
      pannerRef.current.connect(gainRef.current);
      gainRef.current.connect(audioCtxRef.current.destination);
      oscillatorsRef.current = [];
      freqArray.forEach((freq, index) => {
        if (!audioCtxRef.current) return;
        const osc = audioCtxRef.current.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        if (isDual) {
          // Stereo separation: freq1 left, freq2 right for binaural effect
          const individualPanner = audioCtxRef.current.createStereoPanner();
          individualPanner.pan.value = index === 0 ? -0.8 : 0.8;
          osc.connect(individualPanner);
          individualPanner.connect(pannerRef.current!);
        } else {
          osc.connect(pannerRef.current!);
        }
        osc.start();
        oscillatorsRef.current.push(osc);
      });
    } catch (e) {
      console.error('Audio API Error', e);
    }
  };
  const stopAudio = () => {
    if (!audioCtxRef.current || !gainRef.current) return;
    try {
      const ctx = audioCtxRef.current;
      gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, ctx.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(0.001, ctx.currentTime + 1);
      setTimeout(() => {
        oscillatorsRef.current.forEach(osc => {
          try {
            osc.stop();
            osc.disconnect();
          } catch (e) {}
        });
        oscillatorsRef.current = [];
        if (ctx.state !== 'closed') {
          ctx.close().catch(() => {});
        }
      }, 1100);
    } catch (e) {}
  };
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);
  // Auto-start for standalone mode
  useEffect(() => {
    if (isStandalone && !isActive && !isFinished) {
      setIsActive(true);
      startAudio();
    }
  }, [isStandalone]);
  const handleStart = () => {
    playClick();
    setIsActive(true);
    startAudio();
  };
  const handleComplete = () => {
    playClick();
    stopAudio();
    onComplete();
  };
  const handleClose = () => {
    playClick();
    stopAudio();
    if (onClose) onClose();
  };
  const displayFreq = isDual ? `${freqArray[0]} + ${freqArray[1]} Hz` : `${freqArray[0]} Hz`;
  const bgStyle = isDual ? {
    background: `linear-gradient(to bottom, rgba(${colorRGB}, 0.4), rgba(${secondaryColorRGB === '255, 255, 255' ? '100, 100, 140' : secondaryColorRGB}, 0.2), #0f172a)`,
    backgroundColor: '#0f172a'
  } : {
    background: `linear-gradient(to bottom, rgba(${colorRGB}, 0.3), #0f172a)`,
    backgroundColor: '#0f172a'
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col items-center justify-between w-full h-full overflow-hidden text-white p-6" style={bgStyle} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-830">
      <div className="w-full flex justify-between items-center mt-4 z-10" data-id="element-831">
        <div className="flex items-center space-x-3" data-id="element-832">
          {onClose && <button onClick={handleClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white border border-white/20 backdrop-blur-md" data-id="element-833">
              ✕
            </button>}
          {!isStandalone && <div className="text-xs font-bold tracking-widest uppercase opacity-70" data-id="element-834">
              Krok 1: Ciało
            </div>}
        </div>
        {isDual && <span className="text-[10px] font-bold tracking-widest uppercase bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-1" data-id="element-835">
            <Sparkles className="w-3 h-3" fill="currentColor" data-id="element-836" />
            <span data-id="element-837">Premium</span>
          </span>}
        <div className="w-10 h-10" data-id="element-838"></div>
      </div>

      {/* Therapy Info Card */}
      {!isStandalone && (soundName || soundDesc) && <motion.div className="w-full max-w-xs mt-4 z-10" initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-839">
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4" data-id="element-840">
            <div className="flex items-center space-x-2.5 mb-2" data-id="element-841">
              <Music className="w-4 h-4" style={{
            color: `rgb(${colorRGB})`
          }} data-id="element-842" />
              <h3 className="text-sm font-semibold text-white" data-id="element-843">
                {soundName || `Terapia ${displayFreq}`}
              </h3>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border" style={{
            backgroundColor: `rgba(${colorRGB}, 0.15)`,
            color: `rgb(${colorRGB})`,
            borderColor: `rgba(${colorRGB}, 0.3)`
          }} data-id="element-844">
                {displayFreq}
              </span>
            </div>
            {soundDesc && <p className="text-[11px] text-slate-400 leading-relaxed" data-id="element-845">
                {soundDesc}
              </p>}
            <p className="text-[10px] text-slate-500 mt-1 italic flex items-center" data-id="element-846">
              <Headphones className="w-3 h-3 mr-1" data-id="element-847" />
              {isDual ? 'Wymagane słuchawki (dźwięk przestrzenny)' : 'Załóż słuchawki dla najlepszego efektu. Pochyla telefon aby zmenić energię fali.'}
            </p>
          </div>
        </motion.div>}

      <div className="flex flex-col items-center text-center mt-6 z-10 w-full relative" data-id="element-848">
        {/* Floating Energy Rings - disappear when sound starts */}
        <AnimatePresence data-id="element-849">
          {!isActive && !isFinished && <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none" initial={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.6,
          filter: 'blur(12px)'
        }} transition={{
          duration: 1.2,
          ease: [0.4, 0, 0.2, 1]
        }} data-id="element-850">
              <motion.div className="absolute w-44 h-44 border-2 border-emerald-400/50" style={{
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%'
          }} animate={{
            rotate: 360
          }} transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-851" />
              <motion.div className="absolute w-56 h-56 border-2 border-cyan-400/40" style={{
            borderRadius: '60% 40% 30% 70% / 50% 60% 50% 40%'
          }} animate={{
            rotate: -360
          }} transition={{
            duration: 18,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-852" />
              <motion.div className="absolute w-64 h-64 border-2 border-indigo-400/30" style={{
            borderRadius: '50% 50% 60% 40% / 60% 40% 50% 50%'
          }} animate={{
            rotate: 360
          }} transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-853" />
            </motion.div>}
        </AnimatePresence>

        {soundIcon && <span className="text-4xl mb-2 drop-shadow-lg relative z-10" data-id="element-854">
            {soundIcon}
          </span>}
        <h2 className="text-5xl font-light mb-1 drop-shadow-md relative z-10" data-id="element-855">
          {displayFreq}
        </h2>
        {/* Show inline name/desc only in standalone mode (catalog) */}
        {isStandalone && soundName && <p className="text-sm font-bold text-white/90 mt-1 tracking-wide relative z-10" data-id="element-856">
            {soundName}
          </p>}
        {isStandalone && soundDesc && <p className="text-xs text-white/50 mt-0.5 max-w-xs leading-relaxed relative z-10" data-id="element-857">
            {soundDesc}
          </p>}
        {(isStandalone || !soundName && !soundDesc) && <p className="text-[10px] text-white/70 uppercase tracking-widest mt-3 flex items-center justify-center relative z-10" data-id="element-858">
            <Headphones className="w-3 h-3 mr-1" data-id="element-859" />
            {isDual ? 'Wymagane Słuchawki (Dźwięk Przestrzenny)' : 'Załóż słuchawki i przechylaj telefon'}
          </p>}
        {isDual && <p className="text-[9px] text-white/40 mt-1 relative z-10" data-id="element-860">
            Dudnienia różnicowe: {Math.abs(freqArray[1] - freqArray[0])} Hz
          </p>}
      </div>

      <WaveVisualizer frequency={freqArray[0]} frequencies={freqArray} colorRGB={colorRGB} secondaryColorRGB={secondaryColorRGB} isActive={isActive || isFinished} gyroTilt={gyroTilt} data-id="element-861" />

      <div className="mb-10 z-10 w-full max-w-xs flex flex-col items-center space-y-6" data-id="element-862">
        {!isActive && !isFinished ? <button onClick={handleStart} className="w-full bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full py-4 text-sm font-bold hover:bg-white/20 transition-colors" data-id="element-863">
            Rozpocznij Terapię
          </button> : isActive ? <div className="flex flex-col items-center space-y-4 w-full" data-id="element-864">
            <div className="text-3xl font-light tabular-nums" data-id="element-865">
              {Math.floor(timeLeft / 60).toString().padStart(2, '0')}
              :{(timeLeft % 60).toString().padStart(2, '0')}
            </div>
            {isStandalone && <button onClick={handleClose} className="w-full bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full py-3 text-sm font-bold hover:bg-white/20 transition-colors" data-id="element-866">
                Zakończ
              </button>}
          </div> : <motion.button onClick={handleComplete} className="w-full bg-white text-slate-900 rounded-full py-4 text-sm font-bold hover:bg-slate-100 transition-colors flex items-center justify-center space-x-2" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} data-id="element-867">
            <span data-id="element-868">
              {isStandalone ? `Następne (-${cost} ⚡)` : 'Przejdź dalej'}
            </span>
            {!isStandalone && <ArrowRight className="w-4 h-4" data-id="element-869" />}
          </motion.button>}
      </div>
    </motion.div>;
}